import React, { useCallback, useEffect, useState } from 'react';
import { Env, ErrorBanner, Spinner, SuccessBanner } from '../../shared';
import { useUserLookup } from '../../gamification/api/useUserLookup';
import { useUserActions } from '../../gamification/api/useUserActions';
import { UserGamification } from '../../gamification/api/types';
import { FraudDetail } from '../../gamification/components/FraudDetail';
import { TransactionHistory } from '../../gamification/panels/TransactionHistory';

/**
 * Every currently-flagged user, with review and unflag.
 *
 * The gap this fills: `is_flagged` blocks earning, the leaderboard, streaks,
 * visit progress, challenges and payouts, and the client copy for a blocked
 * action is deliberately neutral ("Spin unavailable right now") so the user is
 * never told a signal fired. Economy Health reports only counts per reason, and
 * User Lookup needs the user up front — so before this panel a false positive
 * was invisible from both ends. Nothing expires a flag either; a review is the
 * only way one is ever lifted.
 */
export function FlaggedUsersPanel({ env }: { env: Env }) {
  const { results, loading, error, listFlagged, getDetail, setResults } = useUserLookup(env);
  const actions = useUserActions(env);
  const [notice, setNotice] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [detailLoading, setDetailLoading] = useState<Set<string>>(new Set());
  const [historyOpen, setHistoryOpen] = useState<Set<string>>(new Set());
  const [notes, setNotes] = useState<Record<string, string>>({});

  const refresh = useCallback(() => {
    setNotice(null);
    setExpanded(new Set());
    setHistoryOpen(new Set());
    void listFlagged();
  }, [listFlagged]);

  // There is no search term to enter, so the list loads itself — and reloads
  // when the env selector moves, or the panel would show another stage's users.
  useEffect(refresh, [refresh]);

  const patchUser = (userId: string, patch: Partial<UserGamification>) =>
    setResults(results.map((u) => (u.user_id === userId ? { ...u, ...patch } : u)));

  const withMember = (set: Set<string>, id: string, present: boolean) => {
    const next = new Set(set);
    if (present) next.add(id);
    else next.delete(id);
    return next;
  };

  /** Expanding loads the detail once — the listing returns summaries. */
  const toggleDetail = async (user: UserGamification) => {
    const isOpen = expanded.has(user.user_id);
    setExpanded((prev) => withMember(prev, user.user_id, !isOpen));
    if (isOpen || user.flag_history) return;

    setDetailLoading((prev) => withMember(prev, user.user_id, true));
    const detail = await getDetail(user.user_id);
    setDetailLoading((prev) => withMember(prev, user.user_id, false));
    if (detail) patchUser(user.user_id, detail);
  };

  /**
   * Drops the row rather than marking it unflagged in place: this list is
   * "who is flagged", so a cleared user no longer belongs in it.
   */
  const doUnflag = async (user: UserGamification) => {
    const note = (notes[user.user_id] ?? '').trim();
    const ok = await actions.unflagUser(user.user_id, note || undefined);
    if (!ok) return;
    setResults(results.filter((u) => u.user_id !== user.user_id));
    setNotes((prev) => {
      const next = { ...prev };
      delete next[user.user_id];
      return next;
    });
    setNotice(`Unflagged ${user.display_name || user.user_id}. Earning paths restored.`);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-gray-400">
          {loading
            ? 'Loading flagged users…'
            : `${results.length} flagged user${results.length === 1 ? '' : 's'}`}
        </p>
        <button
          onClick={refresh}
          disabled={loading}
          className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700 disabled:opacity-40"
        >
          Refresh
        </button>
      </div>

      {notice && <SuccessBanner message={notice} />}
      {error && <ErrorBanner message={error} />}
      {actions.error && <ErrorBanner message={actions.error} />}

      {loading && (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      )}

      {!loading && results.length === 0 && !error && (
        <p className="text-sm text-gray-500 py-6 text-center">
          No flagged users. Nobody is currently blocked from earning.
        </p>
      )}

      <div className="space-y-3">
        {results.map((u) => (
          <div key={u.user_id} className="bg-gray-900 border border-gray-800 rounded-xl p-4">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <p className="text-sm font-medium text-white truncate">
                  {u.display_name || 'no display name'}
                </p>
                <p className="font-mono text-xs text-gray-400 mt-0.5">{u.user_id}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {u.phone_masked ?? 'no phone'} · {u.account_status ?? 'unknown'}
                </p>
              </div>
              <div className="text-right">
                <p className="text-lg font-bold text-white tabular-nums">
                  {u.wallet_balance.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">
                  wallet · {u.total_earned.toLocaleString()} earned
                </p>
              </div>
            </div>

            <div className="mt-3 px-3 py-2 bg-red-950/40 border border-red-800/50 rounded-lg text-xs text-red-300">
              Flagged{u.flagged_reason ? `: ${u.flagged_reason}` : ' (no reason recorded)'}
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <button
                onClick={() => toggleDetail(u)}
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                {expanded.has(u.user_id) ? 'Hide review' : 'Review'}
              </button>
              <button
                onClick={() =>
                  setHistoryOpen((prev) => withMember(prev, u.user_id, !prev.has(u.user_id)))
                }
                className="px-3 py-1.5 text-xs font-medium rounded-lg bg-gray-800 text-gray-200 hover:bg-gray-700"
              >
                {historyOpen.has(u.user_id) ? 'Hide transactions' : 'Transactions'}
              </button>
            </div>

            <div className="mt-3 flex flex-col sm:flex-row gap-2">
              <input
                value={notes[u.user_id] ?? ''}
                onChange={(e) => setNotes((prev) => ({ ...prev, [u.user_id]: e.target.value }))}
                placeholder="Review note (optional) — recorded on the audit row"
                className="flex-1 px-3 py-2 bg-gray-950 border border-gray-700 rounded-lg text-white text-sm focus:outline-none focus:border-violet-500"
              />
              <button
                onClick={() => doUnflag(u)}
                disabled={actions.loading}
                className="px-3 py-2 text-xs font-medium rounded-lg bg-emerald-950/60 border border-emerald-800/50 text-emerald-300 hover:bg-emerald-900/50 disabled:opacity-40"
              >
                Unflag
              </button>
            </div>

            {expanded.has(u.user_id) && (
              <FraudDetail user={u} loading={detailLoading.has(u.user_id)} />
            )}

            {historyOpen.has(u.user_id) && (
              <TransactionHistory
                env={env}
                userId={u.user_id}
                onBalanceChange={(bal) => patchUser(u.user_id, { wallet_balance: bal })}
                onNotice={setNotice}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
